import { useEffect, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import { useApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

type ClientType = "individual" | "couple" | "student" | "lead";
type ClientStatus = "active" | "paused" | "archived";

type Client = {
  id: string;
  name: string;
  type: ClientType;
  goal: string | null;
  status: ClientStatus;
  phone: string | null;
  email: string | null;
  notes: string | null;
  session_count: number;
  last_session_at: string | null;
  created_at: string;
};

const TYPE_LABELS: Record<ClientType, string> = {
  individual: "פרטי",
  couple: "זוגי",
  student: "סטודנטית",
  lead: "ליד",
};

const TYPE_DOT: Record<ClientType, string> = {
  individual: "bg-sky-400",
  couple: "bg-violet-400",
  student: "bg-emerald-400",
  lead: "bg-orange-400",
};

const STATUS_LABEL: Record<ClientStatus, string> = {
  active: "פעילה",
  paused: "מושהית",
  archived: "בארכיון",
};

const STATUS_DOT: Record<ClientStatus, string> = {
  active: "bg-emerald-500",
  paused: "bg-amber-500",
  archived: "bg-stone-400",
};

type TabKey = "all" | "individual" | "couple" | "student";

const TABS: { key: TabKey; label: string }[] = [
  { key: "all", label: "הכל" },
  { key: "individual", label: "פרטי" },
  { key: "couple", label: "זוגות" },
  { key: "student", label: "סטודנטיות" },
];

const dateOnlyFmt = new Intl.DateTimeFormat("he-IL", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
});

export function ClientsPage() {
  const api = useApi();
  const navigate = useNavigate();
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<TabKey>("all");

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api("/api/clients");
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = (await res.json()) as Client[];
      setClients(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : "שגיאה בטעינה");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const counts: Record<TabKey, number> = {
    all: clients.length,
    individual: clients.filter((c) => c.type === "individual").length,
    couple: clients.filter((c) => c.type === "couple").length,
    student: clients.filter((c) => c.type === "student").length,
  };

  const visibleClients =
    activeTab === "all"
      ? clients
      : clients.filter((c) => c.type === activeTab);

  return (
    <div className="min-h-svh -mx-6 -my-8 px-6 py-6 sm:py-8 bg-stone-50">
      <div className="mx-auto max-w-5xl">
        {/* Hero */}
        <header className="flex items-end justify-between gap-6 flex-wrap mb-6">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-stone-500">
              מתאמנות
            </p>
            <h1 className="mt-2 text-3xl sm:text-4xl font-bold tracking-[-0.02em] leading-tight text-stone-900">
              כל המתאמנות.
            </h1>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button
                size="lg"
                className="rounded-full h-12 px-6 gap-2 bg-stone-900 hover:bg-stone-800"
              >
                <Plus className="h-4 w-4" />
                מתאמנת חדשה
              </Button>
            </DialogTrigger>
            <AddClientDialog
              onCreated={async () => {
                setDialogOpen(false);
                await load();
              }}
            />
          </Dialog>
        </header>

        {error && (
          <div className="rounded-2xl bg-rose-100/70 px-5 py-4 text-sm text-rose-900 mb-6">
            שגיאה: {error}
          </div>
        )}

        {/* Pill tabs */}
        {!loading && clients.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap mb-6">
            {TABS.map((t) => {
              const active = t.key === activeTab;
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setActiveTab(t.key)}
                  className={cn(
                    "rounded-full px-5 h-10 text-sm font-medium transition-colors",
                    "inline-flex items-center gap-2",
                    active
                      ? "bg-stone-900 text-stone-50"
                      : "bg-stone-200/70 text-stone-700 hover:bg-stone-200",
                  )}
                >
                  {t.label}
                  <span
                    className={cn(
                      "rounded-full px-2 py-0.5 text-[11px] tabular-nums",
                      active
                        ? "bg-stone-50/20 text-stone-50"
                        : "bg-stone-50/80 text-stone-700",
                    )}
                  >
                    {counts[t.key]}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {/* Table or empty state */}
        {loading ? (
          <p className="text-sm text-stone-500">טוען...</p>
        ) : clients.length === 0 ? (
          <div className="rounded-[28px] bg-emerald-100/55 px-8 py-16 text-center">
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-stone-700/65 mb-4">
              ריק
            </p>
            <h3 className="text-3xl sm:text-4xl font-bold tracking-tight text-stone-900 mb-3">
              עוד אין מתאמנות.
            </h3>
            <p className="text-base text-stone-700/85 max-w-md mx-auto leading-relaxed">
              לחצי על "מתאמנת חדשה" כדי להוסיף את הראשונה. השאר ייפתח מעצמו.
            </p>
          </div>
        ) : visibleClients.length === 0 ? (
          <p className="text-sm text-stone-500">אין מתאמנות בקטגוריה הזו.</p>
        ) : (
          <div className="rounded-[24px] bg-white overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[11px] font-semibold uppercase tracking-[0.18em] text-stone-500">
                  <th className="px-6 py-4 text-right font-semibold">שם</th>
                  <th className="px-6 py-4 text-right font-semibold">סוג</th>
                  <th className="px-6 py-4 text-right font-semibold">מטרה</th>
                  <th className="px-6 py-4 text-right font-semibold tabular-nums">
                    מפגשים
                  </th>
                  <th className="px-6 py-4 text-right font-semibold">
                    מפגש אחרון
                  </th>
                  <th className="px-6 py-4 text-right font-semibold">סטטוס</th>
                </tr>
              </thead>
              <tbody>
                {visibleClients.map((c) => (
                  <tr
                    key={c.id}
                    onClick={() => navigate(`/clients/${c.id}`)}
                    className={cn(
                      "border-t border-stone-200/70 cursor-pointer",
                      "hover:bg-stone-50 transition-colors",
                    )}
                  >
                    <td className="px-6 py-4 align-middle">
                      <span className="text-base font-semibold text-stone-900 tracking-tight">
                        {c.name}
                      </span>
                    </td>
                    <td className="px-6 py-4 align-middle">
                      <span className="inline-flex items-center gap-2 text-stone-700">
                        <span
                          className={cn(
                            "h-1.5 w-1.5 rounded-full",
                            TYPE_DOT[c.type],
                          )}
                        />
                        {TYPE_LABELS[c.type]}
                      </span>
                    </td>
                    <td className="px-6 py-4 align-middle text-stone-600 max-w-[20rem] truncate">
                      {c.goal ?? "—"}
                    </td>
                    <td className="px-6 py-4 align-middle font-semibold tabular-nums text-stone-900">
                      {c.session_count}
                    </td>
                    <td
                      className="px-6 py-4 align-middle text-stone-700 tabular-nums"
                      dir="ltr"
                    >
                      {c.last_session_at
                        ? dateOnlyFmt.format(new Date(c.last_session_at))
                        : "—"}
                    </td>
                    <td className="px-6 py-4 align-middle">
                      <span className="inline-flex items-center gap-2 text-stone-700">
                        <span
                          className={cn(
                            "h-1.5 w-1.5 rounded-full",
                            STATUS_DOT[c.status],
                          )}
                        />
                        {STATUS_LABEL[c.status]}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function AddClientDialog({
  onCreated,
}: {
  onCreated: () => void | Promise<void>;
}) {
  const api = useApi();
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [form, setForm] = useState<{
    name: string;
    type: ClientType;
    goal: string;
  }>({
    name: "",
    type: "individual",
    goal: "",
  });

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSubmitting(true);
    setFormError(null);
    try {
      const res = await api("/api/clients", {
        method: "POST",
        body: JSON.stringify({
          name: form.name.trim(),
          type: form.type,
          goal: form.goal.trim() || null,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setForm({ name: "", type: "individual", goal: "" });
      await onCreated();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : "שגיאה ביצירה");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DialogContent dir="rtl" className="sm:max-w-md">
      <DialogHeader>
        <DialogTitle>מתאמנת חדשה</DialogTitle>
        <DialogDescription>
          הוסיפי מתאמנת חדשה לרשימה. שדות נוספים אפשר להשלים בעמוד המתאמנת.
        </DialogDescription>
      </DialogHeader>

      <form onSubmit={onSubmit} className="grid gap-4">
        <div className="space-y-1.5">
          <Label htmlFor="client-name">שם *</Label>
          <Input
            id="client-name"
            required
            autoFocus
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="שם מלא"
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="client-type">סוג</Label>
          <select
            id="client-type"
            value={form.type}
            onChange={(e) =>
              setForm({ ...form, type: e.target.value as ClientType })
            }
            className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          >
            <option value="individual">פרטי</option>
            <option value="couple">זוגי</option>
            <option value="student">סטודנטית</option>
            <option value="lead">ליד</option>
          </select>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="client-goal">מטרה (אופציונלי)</Label>
          <Textarea
            id="client-goal"
            value={form.goal}
            onChange={(e) => setForm({ ...form, goal: e.target.value })}
            placeholder="מה המטרה של העבודה איתה?"
            rows={3}
          />
        </div>

        {formError && (
          <p className="text-sm text-destructive">שגיאה: {formError}</p>
        )}

        <DialogFooter>
          <Button type="submit" disabled={submitting || !form.name.trim()}>
            {submitting ? "שומרת..." : "הוסיפי מתאמנת"}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}
